import React, { Component } from 'react';
import { Header, List, Table, Segment, Divider, Grid } from 'semantic-ui-react';

export default class ProposalsPage extends Component {
  render() {
    return (
      <div>
        <Header as="h1" content="Project proposal" />
        <p>
          <b>Due Date:</b>
          2/3/22
        </p>

        <p>
          For this assignment, you will submit a draft of your project proposal. The proposal should
          aim to be around one full typewritten page (~500 words) with citations in APA format.
          Course staff will provide feedback on the draft to assist in the preparation of a
          final version.
        </p>

        <Segment placeholder>
          <Grid columns={2} relaxed='very' stackable>
            <Grid.Column>
              <Header as="h3" content="Option 1 (Systems)" />
              <p>If you are designing or developing a new technology, your abstract should cover the following topics:</p>
              <List bulleted>
                <List.Item>
                  <i>Problem</i>: What is the problem that you are solving, and why is it important? For examples, read the
                  first couple of paragraphs in the Introduction section of systems/applications papers in usable privacy
                  and security.
                </List.Item>
                <List.Item>
                  <i>Insight and theoretical contribution</i>: What is the unique perspective that you bring to the
                  research? How does it solve the problem? Keep in mind that even impressive technology prototypes are,
                  at their core, answering a research question or establishing a thesis.
                </List.Item>
                <List.Item>
                  <i>System</i>: What is the exact system that you are building to instantiate this insight? What will it
                  do? Include a low- or medium-fidelity sketch of the envisioned system.
                </List.Item>
                <List.Item>
                  <i>Related work</i>: Write a paragraph describing 4-5 pieces of published research, how they inform your
                  project, and where your work transcends this knowledge.
                </List.Item>
                <List.Item>
                  <i>Evaluation plan</i>: how will you know if your system solved the original problem? (This should include
                  the design of your study.) Grounding this in methodologies that other researchers have used (e.g. by
                  drawing from the class readings) is a good idea. There are three major points you should hit here.
                </List.Item>
                <List.Item>
                  <i>Study design</i>: What are you going to do? Be detailed and precise. This can involve a large range
                  of example outputs from your system, a field deployment, or a controlled study.
                </List.Item>
                <List.Item>
                  <i>Measures</i>: How will you know you succeeded? What will you measure? How will you measure it?
                </List.Item>
                <List.Item>
                  <i>Ecological validity</i>: Why does your study answer your research question? Why does your evaluation
                  address your hypothesis? Make sure your study, and the variables you're measuring, properly address
                  the question you are asking.
                </List.Item>
                <List.Item>
                  <i>Recruitment Plan</i>: Most evaluations involve participants. How will you get them? For pilot studies,
                  we suggest you recruit from within the class - "trading" participation with other groups is a great way
                  to learn about what others are doing. For larger studies (e.g. for those not building a system), you
                  need a clear recruitment plan.
                </List.Item>
              </List>
            </Grid.Column>

            <Grid.Column>
              <Header as="h3" content="Option 2 (Social Science)" />
              <p>
                If you are developing a study, a new method, or data analysis, your abstract should cover
                the following topics. Keep in mind that the grading bar for study design and execution here is much higher
                than if you are building a system:
              </p>

              <List bulleted>
                <List.Item>
                  <i>Research question</i>: What are you trying to answer? What are the theoretical contributions of
                  your work to existing research in your area?
                </List.Item>
                <List.Item>
                  <i>Hypothesis</i>: What do you think the answer to your question is, and why? State your hypothesis
                  in terms that you will actually be able to deliver on within the space of a semester. For example,
                  improving someone’s general awareness of security threats may be your ultimate goal, but you may not
                  be able to measure a change in awareness within a semester. In this case, increasing awareness could
                  be part of your motivation, but not your hypothesis. Your hypothesis needs a more proximal measure.
                </List.Item>
                <List.Item>
                  <i>Related work</i>: Write a paragraph describing 4-5 pieces of published research, how they inform
                  your project, and where your work transcends this knowledge.
                </List.Item>
                <List.Item>
                  <i>Method</i>: How will you explore your hypothesis, and why is that the right approach? (This should
                  include the design of your study.) Grounding this in methodologies that other researchers have used
                  (e.g. by drawing from the class readings) is a good idea. There are three major points you should hit
                  here.
                </List.Item>
                <List.Item>
                  <i>Study design</i>: What are you going to do? Be very detailed and precise.
                </List.Item>
                <List.Item>
                  <i>Evaluation</i>: How will you know you succeeded? What will you measure? How will you measure it?
                </List.Item>
                <List.Item>
                  <i>Ecological validity</i>: Why does your study answer your research question? Why does your evaluation
                  address your hypothesis? Make sure your study, and the variables you're measuring, properly address the
                  question you are asking.
                </List.Item>
                <List.Item>
                  <i>Study recruitment plan</i>: How will you get participants for your study? For pilot studies, I suggest
                  you recruit from within the class -- "trading" participation with other groups is a great way to learn
                  about what others are doing. For larger studies (e.g. for those not building a system), you need a clear
                  recruitment plan.
                </List.Item>
              </List>
            </Grid.Column>
          </Grid>

          <Divider vertical>Or</Divider>
        </Segment>

        <p>
          For a guide to the <a href="http://www.apastyle.org/learn/tutorials/basics-tutorial.aspx" target="_blank">APA
          format</a>, go to APA Style. Note that the information on the site is possibly too detailed for the proposal.
        </p>

        <p>
          Here are two examples of excellent project proposals from the first time I taught this class:
          <List bulleted>
            <List.Item><a href="/files/study_proposal.pdf" target="_blank">Social science study proposal</a></List.Item>
            <List.Item><a href="/files/system_proposal.pdf" target="_blank">System building proposal</a></List.Item>
          </List>
        </p>

        <p>
          I encourage you to iterate multiple times on this abstract. You are free to change directions after submitting
          your draft, but the sooner you nail down a direction, the better your project is likely to be.
        </p>

        <p>
          Tips on running effective studies with people:
        </p>

        <List bulleted>
          <List.Item>
            If you have instructions, present them to participants in written form. You'll have a lot on your mind.
            Likely too much to remember to say everything you want. Written instructions also help insure that
            everything is consistent across participants.
          </List.Item>
          <List.Item>
            Remember to instrument your software so that it logs all relevant user actions. How often did people ___?
          </List.Item>
          <List.Item>
            Whatever you try first is almost guaranteed to have bugs so iteratively prototype your experiments just
            like you iteratively prototype interfaces. Test early and often and leave time for iteration in your
            schedule.
          </List.Item>
          <List.Item>
            If you're asking someone to do a task, it helps to provide a good motivating scenario. Appendix A of this
            paper provides a pretty good example of a scenario for a design tool.
          </List.Item>
        </List>

        <a name="deliverable" />

        <Header as="h2" content="Deliverable" />
        <p>
          One typewritten page (~500 words) describing your group's project proposal, submitted on Canvas. Follow the
          instructions for Option 1 or Option 2 above.
        </p>

        <a name="grading" />

        <Header as="h2" content="Grading" />

        <Header as="h3" content="Option 1: Systems Grading Rubric" />
        <Table definition columns={5}>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell />
              <Table.HeaderCell>Insufficient</Table.HeaderCell>
              <Table.HeaderCell>Adequate</Table.HeaderCell>
              <Table.HeaderCell>Proficient</Table.HeaderCell>
              <Table.HeaderCell>Mastery</Table.HeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            <Table.Row>
              <Table.Cell>Problem (4 pts)</Table.Cell>
              <Table.Cell>
                1: The problem is unclear, unimportant or not well-motivated.
              </Table.Cell>
              <Table.Cell>
                2: The problem is clear but only motivated at a mediocre level. The solution feels like a
                hammer trying to find a nail.
              </Table.Cell>
              <Table.Cell>
                3: The problem is clearly articulated and well-motivated.
              </Table.Cell>
              <Table.Cell>
                4: The problem poses a novel perspective or a major opportunity for innovation.
              </Table.Cell>
            </Table.Row>

            <Table.Row>
              <Table.Cell>Insight (4 pts)</Table.Cell>
              <Table.Cell>
                1: The insight is not well described or has been covered previously in prior work.
              </Table.Cell>
              <Table.Cell>
                2: The insight is clear, but makes only an incremental contribution to the field.
              </Table.Cell>
              <Table.Cell>
                3: The insight delivers a reasonably novel idea relative to the literature.
              </Table.Cell>
              <Table.Cell>
                4: The insight makes a significant contribution to the literature.
              </Table.Cell>
            </Table.Row>

            <Table.Row>
              <Table.Cell>System (2 pts)</Table.Cell>
              <Table.Cell colSpan={2}>
                1: The system description is unclear or incomplete.
              </Table.Cell>
              <Table.Cell colSpan={2}>
              2: The main idea of the system is made clear through writeup and sketch.
              </Table.Cell>
            </Table.Row>

            <Table.Row>
              <Table.Cell>Related Work (3 pts)</Table.Cell>
              <Table.Cell>
                1: Major related work is missing.
              </Table.Cell>
              <Table.Cell>
                2: Much of the relevant related work is present, but either some is missing or its
                relationship to this project is weak.
              </Table.Cell>
              <Table.Cell colSpan={2}>
                3: Related work is present and well-represented. Its relationship to the project
                is clear.
              </Table.Cell>
            </Table.Row>

            <Table.Row>
              <Table.Cell>Evaluation (4 pts)</Table.Cell>
              <Table.Cell>
                1: The method is unlikely to lead to an evaluation of the insight or system.
              </Table.Cell>
              <Table.Cell>
                2: The evaluation plan might work, but is vague or has methodological flaws, or there
                are obvious better methods.
              </Table.Cell>
              <Table.Cell>
                3: The evaluation is likely to test the proposed insight or system, but has some minor
                methodological flaws or is overly complicated.
              </Table.Cell>
              <Table.Cell>
                4: The evaluation is methodologically valid, not overly complicated and well thought through
              </Table.Cell>
            </Table.Row>

            <Table.Row>
              <Table.Cell>Feasibility (2 pts)</Table.Cell>
              <Table.Cell colSpan={2}>
                1: Project may need to be substantially scaled back to finish by the end of the semester.
              </Table.Cell>
              <Table.Cell colSpan={2}>
                2: Project can likely be completed within the semester if the team works hard.
              </Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table>

        <Header as="h3" content="Option 2: Social Science Grading Rubric" />
        <Table definition columns={5}>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell />
              <Table.HeaderCell>Insufficient</Table.HeaderCell>
              <Table.HeaderCell>Adequate</Table.HeaderCell>
              <Table.HeaderCell>Proficient</Table.HeaderCell>
              <Table.HeaderCell>Mastery</Table.HeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            <Table.Row>
              <Table.Cell>Research question (4 pts)</Table.Cell>
              <Table.Cell>
                1: The research question is absent or trivial (the answer is obvious).
              </Table.Cell>
              <Table.Cell>
                2: There is a promising question but it is not clearly stated.
              </Table.Cell>
              <Table.Cell>
                3: The question is clearly stated but has only minor impact on the field.
              </Table.Cell>
              <Table.Cell>
                4: The question is clearly stated and its answer has major impact on the field.
              </Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell>Hypothesis (4 pts)</Table.Cell>
              <Table.Cell>
                1: The hypothesis is the same as research question or trivial.
              </Table.Cell>
              <Table.Cell>
                2: There is a hypothesis, but it is unclear, too ambitious, or not clearly motivated.
                (What makes you hypothesize so?)
              </Table.Cell>
              <Table.Cell>
                3: The hypothesis is clearly stated, but is only vaguely motivated. (What makes you
                hypothesize so? Can you ground it in prior work?)
              </Table.Cell>
              <Table.Cell>
                4: The hypothesis is clearly stated and well motivated.
              </Table.Cell>
            </Table.Row>

            <Table.Row>
              <Table.Cell>Related Work (2 pts)</Table.Cell>
              <Table.Cell>
                1: Major related work is missing.
              </Table.Cell>
              <Table.Cell>
                2: Much of the relevant related work is present, but either some is missing or its
                relationship to this project is weak.
              </Table.Cell>
              <Table.Cell colSpan={2}>
                3: Related work is present and well-represented. Its relationship to the project
                is clear.
              </Table.Cell>
            </Table.Row>


            <Table.Row>
              <Table.Cell>Study design (4 pts)</Table.Cell>
              <Table.Cell>
                1: The method is unlikely to lead to an evaluation of the hypothesis.
              </Table.Cell>
              <Table.Cell>
                2: The evaluation plan might work, but is vague or has methodological flaws, or there are obvious better methods.
              </Table.Cell>
              <Table.Cell>
                3: The evaluation is likely to test the proposed hypothesis, but has some minor methodological flaws or is overly complicated.
              </Table.Cell>
              <Table.Cell>
                4: The evaluation is methodologically valid, not overly complicated and well thought through.
              </Table.Cell>
            </Table.Row>


            <Table.Row>
              <Table.Cell>Measures (2 pts)</Table.Cell>
              <Table.Cell colSpan={2}>
                1: The measures being used are not reflective of the concepts being studied, are difficult
                to measure, or are likely to exhibit bias.
              </Table.Cell>
              <Table.Cell colSpan={2}>
                2: The measures being used have ecological validity and construct validity, and can
                be measured.
              </Table.Cell>
            </Table.Row>

            <Table.Row>
              <Table.Cell>Feasibility (2 pts)</Table.Cell>
              <Table.Cell colSpan={2}>
                1: Project may need to be substantially scaled back to finish by the end of the semester.
              </Table.Cell>
              <Table.Cell colSpan={2}>
                2: Project can likely be completed within the semester if the team works hard.
              </Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table>

        <a name="revision" />
        <Header as="h1" content="Project proposal revision" />
        <p>
          <b>Due Date:</b>
          2/15/22
        </p>

        <p>
          This is the final revision of your project proposal. It is weighted much more heavily than
          the draft. The formal requirement is that you revise your project proposal to address my
          comments on your draft.
        </p>

        <Header as="h2" content="Deliverable" />
        <p>
          <a href="#deliverable">The deliverable is the same as with the original proposal.</a>
        </p>

        <Header as="h2" content="Grading" />
        <p>
          <a href="#grading">The rubric is the same as with the original proposal.</a>
        </p>

      </div>
    );
  }
}


// WEBPACK FOOTER //
// ./src/pages/group-project-assignments/proposals-page.js